import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, Search, Image, FileText, Film, File, X, Copy,
  Check, Trash2, ChevronDown, RefreshCw, Plus,
} from 'lucide-react';
import { useProject } from '../../../contexts/ProjectContext';
import { contentApi, type MediaAsset } from '../../../api/content';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatBytes(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getMimeCategory(mime: string): 'image' | 'video' | 'pdf' | 'document' | 'other' {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.includes('word') || mime.includes('document') || mime.includes('sheet') || mime.includes('presentation')) return 'document';
  return 'other';
}

function FileTypeIcon({ mime, size = 24 }: { mime: string; size?: number }) {
  const cat = getMimeCategory(mime);
  const cls = `opacity-60`;
  if (cat === 'image') return <Image size={size} className={cls} />;
  if (cat === 'video') return <Film size={size} className={cls} />;
  if (cat === 'pdf') return <FileText size={size} className={`${cls} text-red-500`} />;
  if (cat === 'document') return <FileText size={size} className={cls} />;
  return <File size={size} className={cls} />;
}

const MIME_FILTERS = [
  { value: '', label: 'All Files' },
  { value: 'image', label: 'Images' },
  { value: 'application/pdf', label: 'PDFs' },
  { value: 'video', label: 'Videos' },
  { value: 'application/', label: 'Documents' },
];

// ── Detail Drawer ──────────────────────────────────────────────────────────────

function AssetDrawer({
  asset,
  workspaceId,
  onClose,
  onUpdated,
  onDeleted,
}: {
  asset: MediaAsset;
  workspaceId: string;
  onClose: () => void;
  onUpdated: (asset: MediaAsset) => void;
  onDeleted: (id: string) => void;
}) {
  const [altText, setAltText] = useState(asset.alt_text);
  const [caption, setCaption] = useState(asset.caption);
  const [tagInput, setTagInput] = useState(asset.tags.join(', '));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const isImage = getMimeCategory(asset.mime_type) === 'image';

  const handleSave = async () => {
    setSaving(true);
    try {
      const tags = tagInput.split(',').map(t => t.trim()).filter(Boolean);
      const updated = await contentApi.updateMedia(workspaceId, asset.id, { alt_text: altText, caption, tags });
      onUpdated(updated);
    } catch (err) {
      console.error('Failed to update media', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(asset.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${asset.filename}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await contentApi.deleteMedia(workspaceId, asset.id);
      onDeleted(asset.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete media', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-80 bg-white border-l border-neutral-200 shadow-2xl z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
        <h3 className="text-sm font-semibold text-neutral-800 truncate pr-2">{asset.filename}</h3>
        <button onClick={onClose} className="p-1 rounded-md hover:bg-neutral-100 transition-colors shrink-0">
          <X size={16} className="text-neutral-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Preview */}
        <div className="bg-neutral-50 border-b border-neutral-100 flex items-center justify-center h-48">
          {isImage ? (
            <img src={asset.url} alt={asset.alt_text || asset.filename} className="max-h-full max-w-full object-contain" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-neutral-400">
              <FileTypeIcon mime={asset.mime_type} size={40} />
              <span className="text-xs">{asset.mime_type}</span>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="px-4 py-3 border-b border-neutral-100 space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-neutral-500">Type</span>
            <span className="text-neutral-700 font-medium">{asset.mime_type}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-neutral-500">Size</span>
            <span className="text-neutral-700 font-medium">{formatBytes(asset.file_size)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-neutral-500">Added</span>
            <span className="text-neutral-700 font-medium">{formatDate(asset.created_at)}</span>
          </div>
        </div>

        {/* URL Copy */}
        <div className="px-4 py-3 border-b border-neutral-100">
          <button
            onClick={handleCopy}
            className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all
              ${copied
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                : 'border-neutral-200 hover:border-neutral-300 text-neutral-700 hover:bg-neutral-50'
              }`}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy URL'}
          </button>
        </div>

        {/* Editable fields */}
        <div className="px-4 py-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-neutral-600 mb-1.5 block">Alt Text</label>
            <input
              value={altText}
              onChange={e => setAltText(e.target.value)}
              placeholder="Describe this image for accessibility"
              className="w-full text-sm px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-400"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-neutral-600 mb-1.5 block">Caption</label>
            <input
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="Optional caption"
              className="w-full text-sm px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-400"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-neutral-600 mb-1.5 block">Tags</label>
            <input
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              placeholder="hero, background, product..."
              className="w-full text-sm px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-400"
            />
            <p className="text-[10px] text-neutral-400 mt-1">Separate with commas</p>
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="px-4 py-3 border-t border-neutral-100 flex items-center justify-between gap-2">
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
        >
          <Trash2 size={13} />
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-coral-500 hover:bg-coral-600 text-white text-xs font-medium transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Upload Zone ────────────────────────────────────────────────────────────────

function UploadZone({
  onUpload,
  uploading,
  compact = false,
}: {
  onUpload: (files: FileList) => void;
  uploading: boolean;
  compact?: boolean;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (compact) {
    return (
      <>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={e => e.target.files && onUpload(e.target.files)} />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-coral-500 hover:bg-coral-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
        >
          <Upload size={14} />
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </>
    );
  }

  return (
    <div
      onDragEnter={() => setDragging(true)}
      onDragLeave={() => setDragging(false)}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDrop={e => {
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files.length) onUpload(e.dataTransfer.files);
      }}
      className={`border-2 border-dashed rounded-2xl p-12 flex flex-col items-center gap-3 cursor-pointer transition-all
        ${dragging ? 'border-coral-400 bg-coral-50' : 'border-neutral-200 hover:border-neutral-300 bg-neutral-50'}`}
      onClick={() => inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" multiple className="hidden" onChange={e => e.target.files && onUpload(e.target.files)} />
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${dragging ? 'bg-coral-100' : 'bg-white border border-neutral-200'}`}>
        <Upload size={20} className={dragging ? 'text-coral-500' : 'text-neutral-400'} />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-neutral-700">Drop files here or click to browse</p>
        <p className="text-xs text-neutral-400 mt-1">Images, PDFs, videos, documents — any file type</p>
      </div>
      {uploading && (
        <div className="flex items-center gap-2 text-sm text-coral-600">
          <RefreshCw size={14} className="animate-spin" />
          Uploading...
        </div>
      )}
    </div>
  );
}

// ── Media Card ─────────────────────────────────────────────────────────────────

function MediaCard({ asset, onClick, selected }: { asset: MediaAsset; onClick: () => void; selected: boolean }) {
  const isImage = getMimeCategory(asset.mime_type) === 'image';
  return (
    <button
      onClick={onClick}
      className={`group relative rounded-xl overflow-hidden border text-left transition-all hover:shadow-md
        ${selected ? 'border-coral-400 ring-2 ring-coral-400/20' : 'border-neutral-200 hover:border-neutral-300'}`}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-neutral-50 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img src={asset.url} alt={asset.alt_text || asset.filename} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-neutral-400 p-4">
            <FileTypeIcon mime={asset.mime_type} size={32} />
            <span className="text-[10px] text-neutral-400 uppercase font-medium">
              {asset.mime_type.split('/')[1]?.slice(0, 6) ?? asset.mime_type.slice(0, 6)}
            </span>
          </div>
        )}
      </div>
      {/* Info */}
      <div className="px-2.5 py-2 border-t border-neutral-100">
        <p className="text-[11px] font-medium text-neutral-700 truncate">{asset.filename}</p>
        <p className="text-[10px] text-neutral-400 mt-0.5">{formatBytes(asset.file_size)}</p>
      </div>
      {/* Tags overlay */}
      {asset.tags.length > 0 && (
        <div className="absolute top-2 left-2 flex flex-wrap gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {asset.tags.slice(0, 2).map(tag => (
            <span key={tag} className="text-[9px] px-1.5 py-0.5 bg-black/60 text-white rounded-full backdrop-blur-sm">
              {tag}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

const MediaBrowserPage = () => {
  const { currentProject } = useProject();
  const workspaceId = currentProject?.workspaceId;

  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [mimeFilter, setMimeFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showMimeDropdown, setShowMimeDropdown] = useState(false);

  const fetchAssets = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const result = await contentApi.listMedia(workspaceId, { search: search || undefined, mime_type: mimeFilter || undefined });
      setAssets(result.assets);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load media', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, search, mimeFilter]);

  useEffect(() => { void fetchAssets(); }, [fetchAssets]);

  const handleUpload = async (files: FileList) => {
    if (!workspaceId) return;
    setUploading(true);
    const newAssets: MediaAsset[] = [];
    for (const file of Array.from(files)) {
      try {
        const asset = await contentApi.uploadMedia(workspaceId, file);
        newAssets.push(asset);
      } catch (err) {
        console.error(`Failed to upload ${file.name}`, err);
      }
    }
    setAssets(prev => [...newAssets.reverse(), ...prev]);
    setTotal(prev => prev + newAssets.length);
    setUploading(false);
  };

  const selectedAsset = assets.find(a => a.id === selectedId) ?? null;
  const currentMimeLabel = MIME_FILTERS.find(f => f.value === mimeFilter)?.label ?? 'All Files';

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-neutral-100">
        <div>
          <h1 className="text-base font-semibold text-neutral-800">Media Library</h1>
          <p className="text-xs text-neutral-400 mt-0.5">{total} asset{total !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3 flex-1 max-w-xl">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by filename or tag..."
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-400"
            />
          </div>
          {/* Mime filter */}
          <div className="relative">
            <button
              onClick={() => setShowMimeDropdown(!showMimeDropdown)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm border border-neutral-200 rounded-lg hover:border-neutral-300 transition-colors"
            >
              {currentMimeLabel}
              <ChevronDown size={13} className="text-neutral-400" />
            </button>
            {showMimeDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMimeDropdown(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-neutral-200 rounded-xl shadow-lg py-1 min-w-[140px]">
                  {MIME_FILTERS.map(f => (
                    <button
                      key={f.value}
                      onClick={() => { setMimeFilter(f.value); setShowMimeDropdown(false); }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors
                        ${mimeFilter === f.value ? 'bg-coral-50 text-coral-700 font-medium' : 'text-neutral-700 hover:bg-neutral-50'}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <UploadZone onUpload={handleUpload} uploading={uploading} compact />
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <RefreshCw size={20} className="text-neutral-400 animate-spin" />
          </div>
        ) : assets.length === 0 ? (
          <div className="max-w-md mx-auto py-16">
            <UploadZone onUpload={handleUpload} uploading={uploading} />
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {/* Upload tile */}
            <UploadTile onUpload={handleUpload} uploading={uploading} />
            {assets.map(asset => (
              <MediaCard
                key={asset.id}
                asset={asset}
                onClick={() => setSelectedId(prev => prev === asset.id ? null : asset.id)}
                selected={selectedId === asset.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selectedAsset && workspaceId && (
        <AssetDrawer
          asset={selectedAsset}
          workspaceId={workspaceId}
          onClose={() => setSelectedId(null)}
          onUpdated={updated => setAssets(prev => prev.map(a => a.id === updated.id ? updated : a))}
          onDeleted={id => setAssets(prev => prev.filter(a => a.id !== id))}
        />
      )}

      {/* Backdrop for drawer */}
      {selectedAsset && (
        <div className="fixed inset-0 z-40 bg-black/10" onClick={() => setSelectedId(null)} />
      )}
    </div>
  );
};

function UploadTile({ onUpload, uploading }: { onUpload: (files: FileList) => void; uploading: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <button
      onClick={() => inputRef.current?.click()}
      disabled={uploading}
      className="aspect-square rounded-xl border-2 border-dashed border-neutral-200 hover:border-coral-400 hover:bg-coral-50 flex flex-col items-center justify-center gap-2 transition-all group disabled:opacity-50"
    >
      <input ref={inputRef} type="file" multiple className="hidden" onChange={e => e.target.files && onUpload(e.target.files)} />
      <div className="w-8 h-8 rounded-lg bg-neutral-100 group-hover:bg-coral-100 flex items-center justify-center transition-colors">
        <Plus size={16} className="text-neutral-400 group-hover:text-coral-500 transition-colors" />
      </div>
      <span className="text-[10px] text-neutral-400 group-hover:text-coral-500 font-medium transition-colors">Upload</span>
    </button>
  );
}

export default MediaBrowserPage;
